import axios from 'axios';
import { redirectToSignIn } from '../auth/redirect';

/**
 * Every call goes through the gateway as POST {gateway}/{service}/{command}, with the
 * Clerk session token attached. The gateway stamps the caller's identity and workspace
 * onto the payload, so nothing here ever sends a user or organisation id: a body that
 * carries `organizationId` would change which workspace the call acts on.
 */
const apiClient = axios.create({
  baseURL: (import.meta.env.VITE_BACKEND_URL as string | undefined) ?? '',
  withCredentials: true,
  // A request the server never answers fails and says so, rather than leaving whatever
  // waits on it waiting for good (QA 2026-09-24, files-doc-F16).
  timeout: 30_000,
});

/** How long a sign-in refresh may take before the call is given up on. */
const TOKEN_WAIT_MS = 10_000;

/** Said when the sign-in refresh never finishes: the person is signed in, the refresh is stuck. */
const TOKEN_STUCK_MESSAGE = 'Couldn’t confirm your sign-in. Check your connection, or reload the page.';

class TokenStuck extends Error {}

type TokenGetter = () => Promise<string | null>;

let getToken: TokenGetter | null = null;

/** Installed during render, not in an effect: effects run child-first and the tree's first calls would go out without a token. */
export function setTokenGetter(fn: TokenGetter): void {
  getToken = fn;
}

export function currentTokenGetter(): TokenGetter | null {
  return getToken;
}

apiClient.interceptors.request.use(async (config) => {
  let token: string | null = null;
  /**
   * A Clerk refresh stuck holding its lock (seen after a tab sat in the background for a
   * long time) never settles, and every call — an upload, a rename — used to wait on it
   * for good with no word (QA 2026-09-24, files-doc-F16). It gets TOKEN_WAIT_MS, then the
   * call fails with a message rather than going out unsigned and being sent to sign in.
   */
  let timer: ReturnType<typeof setTimeout> | undefined;
  try {
    token = getToken
      ? await Promise.race([
          getToken(),
          new Promise<never>((_, reject) => {
            timer = setTimeout(() => reject(new TokenStuck(TOKEN_STUCK_MESSAGE)), TOKEN_WAIT_MS);
          }),
        ])
      : null;
  } catch (err) {
    if (err instanceof TokenStuck) throw err;
    // A refresh that fails mid-session is a dead session: the call below gets a 401 and
    // the response interceptor sends the person back to sign in.
  } finally {
    clearTimeout(timer);
  }
  if (token) config.headers.set('Authorization', `Bearer ${token}`);
  else config.headers.delete('Authorization');
  return config;
});

apiClient.interceptors.response.use(
  (response) => response,
  (error: { response?: { status?: number } }) => {
    if (error?.response?.status === 401) redirectToSignIn();
    return Promise.reject(error);
  },
);

export class ArtworkApiError extends Error {
  constructor(
    readonly cmd: string,
    message: string,
    readonly statusCode: number,
    readonly errorCode: string,
  ) {
    super(message);
    this.name = 'ArtworkApiError';
  }
}

const GATEWAY_FIELDS = ['user', 'userId', 'organizationId', 'contextId', 'clerkId', 'dbUserId', 'dbOrgId', 'email'];

/**
 * What a failed call says. The backend's own refusal (`error`) when it sent one; else words for
 * people, never axios's "Request failed with status code 500" or "Network Error", which reached
 * the screen as they were (Share › Move to project, 6 Oct).
 */
export function failureMessage(err: unknown, response: { status?: number; data?: Record<string, unknown> } | undefined, timedOut: boolean): string {
  const said = response?.data?.error;
  if (typeof said === 'string' && said.trim()) return said;
  if (timedOut) return 'The server took too long to answer. Try again.';
  if (err instanceof TokenStuck) return err.message;
  if (response?.status) return 'Something went wrong on our side. Try again.';
  return 'Couldn’t reach Flobi. Check your connection and try again.';
}

/** Calls one Artwork command, e.g. artworkCmd('artwork.doc.list', { limit: 20 }). */
export async function artworkCmd<T>(cmd: string, body: Record<string, unknown> = {}): Promise<T> {
  const payload: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(body)) {
    if (GATEWAY_FIELDS.includes(key)) {
      if (import.meta.env.DEV) console.warn(`[artworkApi] ${cmd}: dropped ${key}; the gateway sets it`);
      continue;
    }
    if (value !== undefined) payload[key] = value;
  }
  try {
    const response = await apiClient.post(`/artwork/${cmd}`, payload);
    const data = response?.data;
    // A refusal can arrive as HTTP 200 with success: false; it is still an error.
    if (data && data.success === false) {
      throw new ArtworkApiError(cmd, typeof data.error === 'string' && data.error.trim() ? data.error : 'Something went wrong on our side. Try again.', data.statusCode ?? 500, data.errorCode ?? data.errorType ?? 'UNKNOWN');
    }
    return (data && typeof data === 'object' && 'data' in data ? data.data : data) as T;
  } catch (err) {
    if (err instanceof ArtworkApiError) throw err;
    const response = (err as { response?: { status?: number; data?: Record<string, unknown> } })?.response;
    const timedOut = (err as { code?: string })?.code === 'ECONNABORTED' || (err as { code?: string })?.code === 'ETIMEDOUT';
    throw new ArtworkApiError(cmd, failureMessage(err, response, timedOut), response?.status ?? 0, (response?.data?.errorCode as string) ?? (response?.status ? 'HTTP_ERROR' : 'NETWORK_ERROR'));
  }
}

export default apiClient;
